<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>{{ $t("empresas_inmobiliarias.titulo") }}{{ $t(`empresas_de_agentes.tipo.${tipoEmpresaAgenteEnum}`) }}</h1>
      <div class="toolbar">
        <button type="button" @click.prevent="createEmpresaInmobiliaria()" class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>

    <DxDataGrid v-bind="getDefaultGridProperties()" :data-source="gridSource" ref="grid" class="dx-card wide-card grid-operaciones">
      <DxColumnFixing :enabled="true" />
      <DxColumn data-field="Documento" :caption="$t('empresas_inmobiliarias.grid.cif')"></DxColumn>
      <DxColumn data-field="RazonSocial" :caption="$t('empresas_inmobiliarias.grid.razon_social')"></DxColumn>
      <DxColumn data-field="DireccionFisica" :caption="$t('empresas_inmobiliarias.grid.direccion_fisica')"></DxColumn>
      <DxColumn data-field="Telefono" :caption="$t('empresas_inmobiliarias.grid.telefono')"></DxColumn>
      <DxColumn data-field="Web" :caption="$t('empresas_inmobiliarias.grid.sitio_web')"></DxColumn>
      <DxColumn data-field="Email" :caption="$t('empresas_inmobiliarias.grid.email')"></DxColumn>
      <DxColumn data-field="CodigoEstado" :caption="$t('empresas_colaboradoras.grid.estado')" cell-template="estadoTemplate">
        <DxLookup v-bind="getLookup('empresa_agente', 'estado')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="NombreComercial" :caption="$t('empresas_inmobiliarias.grid.comercial')"></DxColumn>
      <DxColumn data-field="Autoregistro" :caption="$t('empresas_inmobiliarias.grid.autoregistro')" cell-template="autoregistroTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="FechaCreacion" :caption="$t('empresas_inmobiliarias.grid.fecha_creacion')"
        data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="FechaValidacion" :caption="$t('empresas_inmobiliarias.grid.fecha_validacion')"
        data-type="datetime" format="dd/MM/yyyy HH:mm"></DxColumn>
      <DxColumn data-field="PuedeTramitarAgua" :caption="$t('empresas_inmobiliarias.grid.puede_tramitar_agua')" cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn type="buttons" :caption="$t('empresas_inmobiliarias.grid.acciones')" :fixed="true"
        fixed-position="right" :buttons="[
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('empresas_inmobiliarias.grid.gestionar'),
          icon: 'fa-solid fa-euro-sign text-success',
          onClick: (e: any) => gestionar(e.row?.data.Id)
        },
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('empresas_inmobiliarias.grid.ver_detalle'),
          icon: 'fa-regular fa-magnifying-glass text-success',
          onClick: (e: any) => verDetalle(e.row?.data.Id)
        },
        //{
        //  cssClass: 'btn btn-link text-decoration-none btn-sm',
        //  text: $t('empresas_inmobiliarias.grid.modificar'),
        //  icon: 'fa-regular fa-edit text-success',
        //  onClick: (e: any) => editar(e.row?.data.Id)
        //},
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('empresas_inmobiliarias.grid.borrar'),
          icon: 'fa-regular fa-trash text-success',
          onClick: (e: any) => borrar(e.row?.data.Id),
          disabled: (options: any) => options.row?.data.TieneUsuariosActivos || options.row?.data.CodigoEstado === EstadoEmpresaAgentesEnum.Baja
        },
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('empresas_inmobiliarias.grid.reactivar'),
          icon: 'fa-regular fa-unlock text-success',
          onClick: (e: any) => reactivar(e.row?.data.Id),
          disabled: (options: any) => options.row?.data.CodigoEstado != EstadoEmpresaAgentesEnum.Baja,
        },
      ]"/>

      <template #autoregistroTemplate="{ data }">
        <BooleanTemplate :value="data.value" />
      </template>

      <template #booleanTemplate="{ data }">
        <BooleanTemplate :value="data.value" />
      </template>
      <template #estadoTemplate="{ data }">
        <div :class="[
          `d-inline-block rounded py-1 px-2`,
          `estado-empresa-agente ${data.value.replace(' ', '')}`,
        ]">
          {{ $t(`empresa_agente.estado.${data.value}`) }}
        </div>
      </template>
    </DxDataGrid>

  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { DxDataGrid, DxColumn, DxColumnFixing, DxLookup } from "devextreme-vue/data-grid";
import { getColumnLookupBoolean, getDefaultGridProperties, getLookup } from "@helpers/grid-helper";
import { useApi } from "@api/integration";
import type CustomStore from "devextreme/data/custom_store";
import { useIntl } from "@services/i18n";
import { useModalService } from "@services/modal-service";
import EmpresaInmobiliariaForm from "@/components/empresas-agentes/EmpresaInmobiliariaForm.vue";
import { CreateEmpresaInmobiliaria } from "@/components/empresas-agentes/models/CreateEmpresaInmobiliaria";
import { messageBoxResult } from '@components/dialogs/message-box';
import { EstadoEmpresaAgentesEnum, TipoEmpresaAgentesEnum, type DXLoadOptions } from "@/.shared/.api";
import { useRouter } from '@services/routing-services';
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import BooleanTemplate from "@/.shared/components/BooleanTemplate.vue";

const intl = useIntl();
const api = useApi();
const grid = ref<DxDataGrid>();
const gridSource = ref<CustomStore>();
const modal = useModalService();
const router = useRouter();

const props = defineProps({
  tipoEmpresaAgente: String,
});

const tipoEmpresaAgenteEnum = computed(() => props.tipoEmpresaAgente?.toUpperCase());

onMounted(async () => {
  setDataSource();
});

async function createEmpresaInmobiliaria() {
  let title = `${intl.i18n.t("empresa_inmobiliaria.creacion.titulo")}`;
  modal
    .showModal(EmpresaInmobiliariaForm, { model: new CreateEmpresaInmobiliaria(), isEditMode: false }, { title: title, width: 800, height: 720 })
    .then((value) => {
      if (value == messageBoxResult.ok) grid.value?.instance?.refresh();
    });
}

const setDataSource = async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const idsEstados = [EstadoEmpresaAgentesEnum.Validada, EstadoEmpresaAgentesEnum.Baja]; 
      const q = p as DXLoadOptions;

      return await api.callStandardApi("getEmpresasInmobiliariasForGrid", { showLoading: false }, q, idsEstados);
    }
  });
};

const gestionar = async (idEmpresaInmobiliaria: string) => {
  await router.push(intl.i18Route({ name: "empresas-inmobiliarias-gestionar", params: { idEmpresaInmobiliaria: idEmpresaInmobiliaria } }));
};

const verDetalle = async (idEmpresaInmobiliaria: string) => {
  await router.push({ name: "empresa-agentes", query: { idEmpresaAgentes: idEmpresaInmobiliaria, tipoEmpresaAgente: TipoEmpresaAgentesEnum.Inmobiliaria } });
};

const editar = async (idEmpresaInmobiliaria: string) => {
  let title = `${intl.i18n.t("empresas_inmobiliarias.edicion.titulo")}`;
  modal
    .showModal(
      EmpresaInmobiliariaForm,
      { model: await api.callStandardApi("getEmpresaInmobiliariaById", { showLoading: true }, idEmpresaInmobiliaria), isEditMode: true },
      { title: title, width: 800, height: 720 },
    )
    .then((value) => {
      if (value == messageBoxResult.ok) grid.value?.instance?.refresh();
    });
};

const borrar = async (idEmpresaInmobiliaria: string) => {
  let respuesta = await modal.messageBox(
    intl.i18n.t("empresas_inmobiliarias.grid.borrar_pregunta"),
    intl.i18n.t("empresas_inmobiliarias.grid.titulo_confirma_borrar"),
    "okCancel",
    "warning",
  );
  if (respuesta == messageBoxResult.ok)
    await api
      .callStandardApi("deleteEmpresaInmobiliaria", { showLoading: true }, idEmpresaInmobiliaria)
      .then(() => {
        toastSuccess(intl.i18n.t("empresa_inmobiliaria.borrado_exito"));
        grid.value?.instance?.refresh();
      })
      .catch(() => toastError(intl.i18n.t("empresa_inmobiliaria.borrado_fallo")));
};

const reactivar = async (idEmpresaInmobiliaria: string) => {
  let respuesta = await modal.messageBox(
    intl.i18n.t("empresas_inmobiliarias.grid.reactivar_pregunta"),
    intl.i18n.t("empresas_inmobiliarias.grid.titulo_confirma_reactivar"),
    "okCancel",
    "warning",
  );
  if (respuesta == messageBoxResult.ok)
    await api
      .callStandardApi("reactivateEmpresaAgentes", { showLoading: true }, idEmpresaInmobiliaria)
      .then(() => {
        toastSuccess(intl.i18n.t("empresas_inmobiliarias.reactivado_exito"));
        grid.value?.instance?.refresh();
      })
      .catch(() => toastError(intl.i18n.t("empresas_inmobiliarias.reactivado_fallo")));
};
</script>

<style lang="scss" scoped>


</style>
